import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload as ImportIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  PeopleAlt as PeopleIcon,
  MailOutline as MailIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Visibility as PreviewIcon,
  UploadFile as UploadIcon,
  Warning as WarningIcon,
  Insights as InsightsIcon,
  BarChart as BarChartIcon,
  Block as RejectIcon,
  Paid as PaidIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { communityAPI, groupAPI, inviteAPI, tenantAdminAPI } from '../../services/api';
import { getCurrentUser } from '../../services/auth';
import StudentPerformanceDashboard from './StudentPerformanceDashboard';

const emptyGroupForm = {
  name: '',
  description: '',
  cohortCode: '',
  academicYear: '',
  tags: [],
  schedule: {
    startsAt: '',
    endsAt: '',
    cadence: '',
  },
};

const defaultCreditInvoiceForm = {
  quantity: '',
};

const defaultCreditAllocationForm = {
  targetType: 'cohort',
  amount: '',
  groupIds: [],
  identifiers: '',
  note: '',
};

const defaultCaseManagementSettings = {
  allowStudentLibraryEdits: false,
  allowPersonalLibraryInUnity: true,
  allowDirectVirtualMedic: false,
  community: {
    studentPublishingScope: 'none',
    requireFacultyApproval: true,
    allowTenantCommunity: true,
    allowGlobalCommunity: true,
    allowImports: true,
  },
};

const normalizeCaseManagementPayload = (payload = {}) => ({
  ...defaultCaseManagementSettings,
  ...payload,
  community: {
    ...defaultCaseManagementSettings.community,
    ...(payload.community || {}),
  },
});

const allocationModeOptions = [
  { value: 'assigned', label: 'Assigned credits (per-student wallets)' },
  { value: 'shared_pool', label: 'Shared pool (students draw from central balance)' },
];

const TAB_COUNT = 8;
const COMMUNITY_TAB_INDEX = 7;

const isEditableElement = (element) => {
  if (!element || typeof element !== 'object') {
    return false;
  }
  const tagName = element.tagName?.toLowerCase();
  if (tagName && ['input', 'textarea', 'select'].includes(tagName)) {
    return true;
  }
  if (element.isContentEditable) {
    return true;
  }
  const role = typeof element.getAttribute === 'function' ? element.getAttribute('role') : null;
  if (role && ['textbox', 'combobox'].includes(role)) {
    return true;
  }
  const hasPopup = typeof element.getAttribute === 'function' ? element.getAttribute('aria-haspopup') : null;
  if (hasPopup === 'listbox') {
    return true;
  }
  if (typeof element.closest === 'function') {
    if (element.closest('input, textarea, select, [contenteditable="true"]')) {
      return true;
    }
    if (element.closest('[role="textbox"], [role="combobox"], [aria-haspopup="listbox"]')) {
      return true;
    }
  }
  return false;
};

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box sx={{ py: 3 }}>
      {children}
    </Box>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY-MM-DD');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY-MM-DD HH:mm');
};

const getCaseAuthorName = (caseItem) => {
  const author = caseItem?.author || {};
  const name = `${author.firstName || ''} ${author.lastName || ''}`.trim();
  return name || author.email || 'Unknown';
};

const formatNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return new Intl.NumberFormat().format(numeric);
};

const formatCurrency = (amountCents, currency = 'GBP') => {
  const normalizedCurrency = (currency || 'GBP').toString().toUpperCase();
  const amount = Number(amountCents) / 100;
  if (!Number.isFinite(amount)) {
    return `${normalizedCurrency} 0.00`;
  }
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${normalizedCurrency} ${amount.toFixed(2)}`;
  }
};

const getMemberKey = (member) => {
  if (!member || !member.id) {
    return '';
  }

  return `${member.type || 'user'}:${member.id}`;
};

const UniversityAdminConsole = () => {
  const [tab, setTab] = useState(0);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSummary, setMemberSummary] = useState(null);
  const [memberFilters, setMemberFilters] = useState({ role: 'student', status: 'active', search: '', groupId: 'all', sort: 'name' });
  const [memberSelection, setMemberSelection] = useState(new Set());
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [memberAlert, setMemberAlert] = useState(null);
  const [memberRoleDialogOpen, setMemberRoleDialogOpen] = useState(false);
  const [memberRoleDialogValue, setMemberRoleDialogValue] = useState('student');
  const [memberGroupDialogOpen, setMemberGroupDialogOpen] = useState(false);
  const [memberGroupSelection, setMemberGroupSelection] = useState([]);
  const [memberGroupMode, setMemberGroupMode] = useState('append');
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [inviteBulkLoading, setInviteBulkLoading] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDialogMode, setGroupDialogMode] = useState('create');
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupAlert, setGroupAlert] = useState(null);

  const [rosterPreview, setRosterPreview] = useState(null);
  const [rosterImportId, setRosterImportId] = useState(null);
  const [rosterSelections, setRosterSelections] = useState(new Set());
  const [rosterDefaultGroups, setRosterDefaultGroups] = useState([]);
  const [rosterAutoCreate, setRosterAutoCreate] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterAlert, setRosterAlert] = useState(null);
  const [rosterHistory, setRosterHistory] = useState([]);

  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardAlert, setDashboardAlert] = useState(null);

  const [invitePolicy, setInvitePolicy] = useState(null);
  const [invitePolicyLoading, setInvitePolicyLoading] = useState(false);
  const [invitePolicyError, setInvitePolicyError] = useState('');
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteAlert, setInviteAlert] = useState(null);
  const [inviteFilters, setInviteFilters] = useState({ status: 'pending', role: 'all' });
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'student', expiresInDays: 14, firstName: '', lastName: '' });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteActions, setInviteActions] = useState({ resending: null, revoking: null });
  const [lastInviteLink, setLastInviteLink] = useState('');
  const [creditOverview, setCreditOverview] = useState(null);
  const [creditOverviewLoading, setCreditOverviewLoading] = useState(true);
  const [creditOverviewAlert, setCreditOverviewAlert] = useState(null);
  const [creditInvoiceForm, setCreditInvoiceForm] = useState(defaultCreditInvoiceForm);
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  const [creditInvoices, setCreditInvoices] = useState([]);
  const [creditInvoicesLoading, setCreditInvoicesLoading] = useState(false);
  const [creditInvoiceAlert, setCreditInvoiceAlert] = useState(null);
  const [allocationModeDraft, setAllocationModeDraft] = useState('assigned');
  const [allocationModeSaving, setAllocationModeSaving] = useState(false);
  const [creditAllocationForm, setCreditAllocationForm] = useState(defaultCreditAllocationForm);
  const [creditAllocationLoading, setCreditAllocationLoading] = useState(false);
  const [creditAllocationAlert, setCreditAllocationAlert] = useState(null);
  const [creditAllocationResult, setCreditAllocationResult] = useState(null);
  const [sendingRosterInvites, setSendingRosterInvites] = useState(null);
  const [caseManagement, setCaseManagement] = useState(null);
  const [caseManagementDraft, setCaseManagementDraft] = useState(null);
  const [caseManagementLoading, setCaseManagementLoading] = useState(false);
  const [caseManagementSaving, setCaseManagementSaving] = useState(false);
  const [caseManagementAlert, setCaseManagementAlert] = useState(null);
  const [communityQueue, setCommunityQueue] = useState({ pending: [], flagged: [] });
  const [communityQueueLoading, setCommunityQueueLoading] = useState(false);
  const [communityQueueAlert, setCommunityQueueAlert] = useState(null);
  const [communityNoteDrafts, setCommunityNoteDrafts] = useState({});
  const [communityActionLoading, setCommunityActionLoading] = useState(null);

  const currentUser = useMemo(() => getCurrentUser(), []);
  const isAdmin = currentUser?.role === 'admin';

  const sortedMembers = useMemo(() => {
    const records = Array.isArray(members) ? [...members] : [];
    const sortKey = memberFilters.sort;

    const getName = (member) => {
      if (!member) return '';
      if (member.displayName) return member.displayName;
      const composed = `${member.firstName || ''} ${member.lastName || ''}`.trim();
      if (composed) return composed;
      if (member.metadata?.firstName || member.metadata?.lastName) {
        return `${member.metadata.firstName || ''} ${member.metadata.lastName || ''}`.trim();
      }
      return member.email || member.id || '';
    };

    if (sortKey === 'recent') {
      records.sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
    } else if (sortKey === 'role') {
      records.sort((a, b) => {
        const aRole = `${a.type}:${a.role || ''}`;
        const bRole = `${b.type}:${b.role || ''}`;
        return aRole.localeCompare(bRole, undefined, { sensitivity: 'base' });
      });
    } else {
      records.sort((a, b) => getName(a).localeCompare(getName(b), undefined, { sensitivity: 'base' }));
    }

    return records;
  }, [members, memberFilters.sort]);

  const selectedMembers = useMemo(() => sortedMembers.filter((member) => memberSelection.has(getMemberKey(member))), [sortedMembers, memberSelection]);
  const selectedUsers = useMemo(() => selectedMembers.filter((member) => member.type === 'user'), [selectedMembers]);
  const selectedInvites = useMemo(() => selectedMembers.filter((member) => member.type === 'invite'), [selectedMembers]);

  const inviteStatusOptions = useMemo(() => ([
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'expired', label: 'Expired' },
    { value: 'revoked', label: 'Revoked' },
    { value: 'all', label: 'All statuses' },
  ]), []);

  const inviteRoleOptions = useMemo(() => ([
    { value: 'all', label: 'All roles' },
    { value: 'student', label: 'Students' },
    { value: 'instructor', label: 'Instructors' },
  ]), []);

  const memberRoleFilterOptions = useMemo(() => ([
    { value: 'student', label: 'Students' },
    { value: 'instructor', label: 'Instructors' },
    { value: 'staff', label: 'Staff' },
    { value: 'all', label: 'All roles' },
  ]), []);

  const memberStatusOptions = useMemo(() => ([
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'invited', label: 'Pending invites' },
    { value: 'all', label: 'All statuses' },
  ]), []);

  const memberSortOptions = useMemo(() => ([
    { value: 'name', label: 'Name' },
    { value: 'recent', label: 'Most recent' },
    { value: 'role', label: 'Role' },
  ]), []);

  const memberSummaryCards = useMemo(() => ([
    { label: 'Active students', value: memberSummary?.counts?.students || 0 },
    { label: 'Instructors', value: memberSummary?.counts?.instructors || 0 },
    { label: 'Staff', value: memberSummary?.counts?.staff || 0 },
    { label: 'Pending invites', value: memberSummary?.counts?.pendingInvites || 0 },
    { label: 'Inactive', value: memberSummary?.counts?.inactive || 0 },
  ]), [memberSummary]);

  const memberSelectionState = useMemo(() => ({
    total: sortedMembers.length,
    selectedCount: selectedMembers.length,
    allSelected: sortedMembers.length > 0 && selectedMembers.length === sortedMembers.length,
    indeterminate: selectedMembers.length > 0 && selectedMembers.length < sortedMembers.length,
  }), [sortedMembers, selectedMembers]);

  const showMemberAlert = useCallback((severity, message) => {
    setMemberAlert({ severity, message });
    setTimeout(() => setMemberAlert(null), 6000);
  }, []);

  const showGroupAlert = useCallback((severity, message) => {
    setGroupAlert({ severity, message });
    setTimeout(() => setGroupAlert(null), 6000);
  }, []);

  const showRosterAlert = useCallback((severity, message) => {
    setRosterAlert({ severity, message });
    setTimeout(() => setRosterAlert(null), 6000);
  }, []);

  const showInviteAlert = useCallback((severity, message) => {
    setInviteAlert({ severity, message });
    setTimeout(() => setInviteAlert(null), 6000);
  }, []);

  const showCaseManagementAlert = useCallback((severity, message) => {
    setCaseManagementAlert({ severity, message });
    setTimeout(() => setCaseManagementAlert(null), 6000);
  }, []);

  const showCommunityQueueAlert = useCallback((severity, message) => {
    setCommunityQueueAlert({ severity, message });
    setTimeout(() => setCommunityQueueAlert(null), 6000);
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const response = await groupAPI.list();
      setGroups(response.data || []);
    } catch (error) {
      showGroupAlert('error', error.response?.data?.message || 'Failed to load groups');
    }
  }, [showGroupAlert]);

  const loadMembers = useCallback(async () => {
    setMemberLoading(true);
    setMemberError('');
    try {
      const params = { role: memberFilters.role, status: memberFilters.status };
      if (memberFilters.search) {
        params.search = memberFilters.search;
      }
      if (memberFilters.groupId && memberFilters.groupId !== 'all') {
        params.groupId = memberFilters.groupId;
      }

      const response = await tenantAdminAPI.listMembers(params);
      const data = response.data || response;
      const records = Array.isArray(data.members) ? data.members : [];
      setMembers(records);
      setMemberSummary(data.summary || null);
      setMemberSelection((previous) => {
        if (!(previous instanceof Set) || previous.size === 0) {
          return new Set();
        }
        const next = new Set();
        records.forEach((member) => {
          const key = getMemberKey(member);
          if (previous.has(key)) {
            next.add(key);
          }
        });
        return next;
      });
    } catch (error) {
      setMemberError(error.response?.data?.message || 'Failed to load roster members');
    } finally {
      setMemberLoading(false);
    }
  }, [memberFilters]);

  const loadRosterHistory = useCallback(async () => {
    try {
      const response = await tenantAdminAPI.listRosterImports();
      setRosterHistory(response.data?.imports || []);
    } catch (error) {
      showRosterAlert('warning', error.response?.data?.message || 'Failed to load roster activity');
    }
  }, [showRosterAlert]);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const response = await tenantAdminAPI.dashboard();
      setDashboard(response.data);
      setDashboardAlert(null);
    } catch (error) {
      setDashboardAlert(error.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const loadInvitePolicy = useCallback(async () => {
    setInvitePolicyLoading(true);
    try {
      const response = await tenantAdminAPI.getRegistrationPolicy();
      const data = response.data || response;
      setInvitePolicy(data);
      setInvitePolicyError('');
    } catch (error) {
      setInvitePolicyError(error.response?.data?.message || 'Failed to load invitation policy');
    } finally {
      setInvitePolicyLoading(false);
    }
  }, []);

  const loadInvites = useCallback(async () => {
    setInvitesLoading(true);
    try {
      const params = { limit: 75 };
      if (inviteFilters.status && inviteFilters.status !== 'all') {
        params.status = inviteFilters.status;
      }
      if (inviteFilters.role && inviteFilters.role !== 'all') {
        params.role = inviteFilters.role;
      }
      const response = await inviteAPI.list(params);
      setInvites(response.data?.invites || []);
    } catch (error) {
      showInviteAlert('error', error.response?.data?.message || 'Failed to load invites');
    } finally {
      setInvitesLoading(false);
    }
  }, [inviteFilters, showInviteAlert]);

  const mergeCaseManagementDraft = useCallback((updates) => {
    setCaseManagementDraft((prev) => {
      const base = prev || defaultCaseManagementSettings;
      const nextCommunity = updates.community
        ? {
          ...defaultCaseManagementSettings.community,
          ...(base.community || {}),
          ...updates.community,
        }
        : {
          ...defaultCaseManagementSettings.community,
          ...(base.community || {}),
        };

      return {
        ...defaultCaseManagementSettings,
        ...base,
        ...updates,
        community: nextCommunity,
      };
    });
  }, []);

  const loadCaseManagementSettings = useCallback(async () => {
    setCaseManagementLoading(true);
    try {
      const response = await tenantAdminAPI.getCaseManagementSettings();
      const data = response.data || response;
      const settings = normalizeCaseManagementPayload(data.caseManagement || {});
      setCaseManagement(settings);
      setCaseManagementDraft(settings);
      setCaseManagementAlert(null);
    } catch (error) {
      showCaseManagementAlert('error', error.response?.data?.message || 'Failed to load case management settings');
    } finally {
      setCaseManagementLoading(false);
    }
  }, [showCaseManagementAlert]);

  const loadCommunityQueue = useCallback(async () => {
    setCommunityQueueLoading(true);
    try {
      const [pendingResult, flaggedResult] = await Promise.allSettled([
        communityAPI.listCases({
          scope: 'tenant',
          listingState: 'pending',
          sortBy: 'newest',
          limit: 50,
        }),
        communityAPI.listCases({
          scope: 'tenant',
          flagged: true,
          sortBy: 'newest',
          limit: 50,
        }),
      ]);

      const nextQueue = {
        pending: pendingResult.status === 'fulfilled' ? (pendingResult.value.data?.cases || []) : [],
        flagged: flaggedResult.status === 'fulfilled' ? (flaggedResult.value.data?.cases || []) : [],
      };
      setCommunityQueue(nextQueue);

      const pendingError = pendingResult.status === 'rejected' ? pendingResult.reason : null;
      const flaggedError = flaggedResult.status === 'rejected' ? flaggedResult.reason : null;
      const error = pendingError || flaggedError;
      if (error) {
        const message = error.response?.data?.message || 'Failed to load community moderation queue';
        const severity = error.response?.status === 403 ? 'info' : 'error';
        showCommunityQueueAlert(severity, message);
      } else {
        setCommunityQueueAlert(null);
      }
    } finally {
      setCommunityQueueLoading(false);
    }
  }, [showCommunityQueueAlert]);

  const saveCaseManagementSettings = async () => {
    if (!caseManagementDraft) {
      return;
    }
    setCaseManagementSaving(true);
    try {
      const response = await tenantAdminAPI.updateCaseManagementSettings(caseManagementDraft);
      const data = response.data || response;
      const settings = normalizeCaseManagementPayload(data.caseManagement || caseManagementDraft);
      setCaseManagement(settings);
      setCaseManagementDraft(settings);
      showCaseManagementAlert('success', 'Case management settings updated.');
    } catch (error) {
      showCaseManagementAlert('error', error.response?.data?.message || 'Failed to update case management settings.');
    } finally {
      setCaseManagementSaving(false);
    }
  };

  const handleCommunityNoteChange = (caseId, value) => {
    setCommunityNoteDrafts((prev) => ({
      ...prev,
      [caseId]: value,
    }));
  };

  const clearCommunityNote = (caseId) => {
    setCommunityNoteDrafts((prev) => {
      const next = { ...prev };
      delete next[caseId];
      return next;
    });
  };

  const handleApproveCommunityCase = async (caseId) => {
    setCommunityActionLoading(caseId);
    try {
      const notes = communityNoteDrafts[caseId] || '';
      await communityAPI.approveCase(caseId, { approvalNotes: notes });
      clearCommunityNote(caseId);
      showCommunityQueueAlert('success', 'Community case approved.');
      await loadCommunityQueue();
    } catch (error) {
      showCommunityQueueAlert('error', error.response?.data?.message || 'Failed to approve community case.');
    } finally {
      setCommunityActionLoading(null);
    }
  };

  const handleRejectCommunityCase = async (caseId) => {
    setCommunityActionLoading(caseId);
    try {
      const notes = communityNoteDrafts[caseId] || '';
      await communityAPI.rejectCase(caseId, { approvalNotes: notes });
      clearCommunityNote(caseId);
      showCommunityQueueAlert('success', 'Community case rejected.');
      await loadCommunityQueue();
    } catch (error) {
      showCommunityQueueAlert('error', error.response?.data?.message || 'Failed to reject community case.');
    } finally {
      setCommunityActionLoading(null);
    }
  };

  const handleArchiveCommunityCase = async (caseId) => {
    setCommunityActionLoading(caseId);
    try {
      await communityAPI.withdrawCase(caseId);
      clearCommunityNote(caseId);
      showCommunityQueueAlert('success', 'Community listing archived.');
      await loadCommunityQueue();
    } catch (error) {
      showCommunityQueueAlert('error', error.response?.data?.message || 'Failed to archive community case.');
    } finally {
      setCommunityActionLoading(null);
    }
  };


  const handleMemberFilterChange = (field, value) => {
    setMemberFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleMemberSearchChange = (event) => {
    const value = event.target.value;
    setMemberFilters((prev) => ({ ...prev, search: value }));
  };

  const toggleMemberSelection = (member) => {
    const key = getMemberKey(member);
    if (!key) {
      return;
    }
    setMemberSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAllMembers = (checked) => {
    if (!checked) {
      setMemberSelection(new Set());
      return;
    }

    const next = new Set();
    sortedMembers.forEach((member) => {
      next.add(getMemberKey(member));
    });
    setMemberSelection(next);
  };

  const clearMemberSelection = () => {
    setMemberSelection(new Set());
  };

  const handleInviteFilterChange = (field, value) => {
    setInviteFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleInviteFormChange = (field, value) => {
    setInviteForm((prev) => ({ ...prev, [field]: value }));
  };

  const openMemberRoleDialog = () => {
    if (selectedUsers.length) {
      setMemberRoleDialogValue(selectedUsers[0].role || 'student');
    } else {
      setMemberRoleDialogValue('student');
    }
    setMemberRoleDialogOpen(true);
  };

  const submitMemberRoleDialog = async () => {
    if (!selectedUsers.length) {
      showMemberAlert('warning', 'Select at least one active member to change roles.');
      return;
    }

    setMemberActionLoading(true);
    try {
      await tenantAdminAPI.updateMemberRoles({
        memberIds: selectedUsers.map((member) => member.id),
        role: memberRoleDialogValue,
      });
      showMemberAlert('success', 'Member roles updated successfully.');
      setMemberRoleDialogOpen(false);
      clearMemberSelection();
      await loadMembers();
    } catch (error) {
      showMemberAlert('error', error.response?.data?.message || 'Failed to update member roles.');
    } finally {
      setMemberActionLoading(false);
    }
  };

  const openMemberGroupDialog = () => {
    setMemberGroupSelection([]);
    setMemberGroupMode('append');
    setMemberGroupDialogOpen(true);
  };

  const submitMemberGroupDialog = async () => {
    if (!selectedUsers.length) {
      showMemberAlert('warning', 'Select at least one member to update cohort assignments.');
      return;
    }

    setMemberActionLoading(true);
    try {
      await tenantAdminAPI.assignMembersToGroups({
        memberIds: selectedUsers.map((member) => member.id),
        groupIds: memberGroupSelection,
        mode: memberGroupMode,
      });
      showMemberAlert('success', 'Cohort memberships updated.');
      setMemberGroupDialogOpen(false);
      clearMemberSelection();
      await Promise.all([loadMembers(), loadGroups()]);
    } catch (error) {
      showMemberAlert('error', error.response?.data?.message || 'Failed to update cohort memberships.');
    } finally {
      setMemberActionLoading(false);
    }
  };

  const handleResendSelectedInvites = async () => {
    if (!selectedInvites.length) {
      showMemberAlert('warning', 'Select pending invites to resend.');
      return;
    }

    setInviteBulkLoading(true);
    try {
      await Promise.all(selectedInvites.map((invite) => inviteAPI.resend(invite.id)));
      showMemberAlert('success', 'Invites resent successfully.');
      clearMemberSelection();
      await loadMembers();
      if (tab === 2) {
        await loadInvites();
      }
    } catch (error) {
      showMemberAlert('error', error.response?.data?.message || 'Failed to resend invites.');
    } finally {
      setInviteBulkLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteForm.email.trim()) {
      showInviteAlert('warning', 'Email is required to send an invite.');
      return;
    }

    setCreatingInvite(true);
    try {
      const expiresInDays = Math.max(Number(inviteForm.expiresInDays) || 14, 1);
      const expiresAt = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString();
      const payload = {
        email: inviteForm.email.trim(),
        role: inviteForm.role,
        expiresAt,
        metadata: {
          firstName: inviteForm.firstName?.trim() || undefined,
          lastName: inviteForm.lastName?.trim() || undefined,
          source: 'tenant-admin-console',
        },
      };

      const response = await inviteAPI.create(payload);
      const createdInvite = response.data?.invite;
      setInvites((prev) => (createdInvite ? [createdInvite, ...prev] : prev));
      setInviteForm((prev) => ({ ...prev, email: '', firstName: '', lastName: '' }));
      setLastInviteLink(createdInvite?.inviteUrl || '');
      showInviteAlert('success', 'Invite sent successfully.');
      await loadInvitePolicy();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send invite.';
      showInviteAlert('error', message);
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }

    setInviteActions((prev) => ({ ...prev, resending: inviteId }));
    try {
      await inviteAPI.resend(inviteId);
      showInviteAlert('success', 'Invite resent successfully.');
      await loadInvites();
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to resend invite.';
      showInviteAlert('error', message);
    } finally {
      setInviteActions((prev) => ({ ...prev, resending: null }));
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }

    setInviteActions((prev) => ({ ...prev, revoking: inviteId }));
    try {
      await inviteAPI.revoke(inviteId);
      showInviteAlert('success', 'Invite revoked.');
      await loadInvites();
      await loadInvitePolicy();
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to revoke invite.';
      showInviteAlert('error', message);
    } finally {
      setInviteActions((prev) => ({ ...prev, revoking: null }));
    }
  };

  const handleCopyInviteLink = async (link) => {
    if (!link) {
      showInviteAlert('info', 'Send the invite to generate a shareable link.');
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      showInviteAlert('success', 'Invite link copied to clipboard.');
    } catch (error) {
      showInviteAlert('warning', 'Unable to copy invite link on this device.');
    }
  };

  const loadCreditOverview = useCallback(async () => {
    setCreditOverviewLoading(true);
    setCreditOverviewAlert(null);
    try {
      const response = await tenantAdminAPI.getCreditOverview();
      const data = response.data || response;
      setCreditOverview(data);
      setAllocationModeDraft(data.creditAllocationMode || 'assigned');
    } catch (error) {
      setCreditOverviewAlert({
        severity: 'error',
        message: error.response?.data?.message || 'Failed to load credit overview.',
      });
    } finally {
      setCreditOverviewLoading(false);
    }
  }, []);

  const loadCreditInvoices = useCallback(async () => {
    setCreditInvoicesLoading(true);
    setCreditInvoiceAlert(null);
    try {
      const response = await tenantAdminAPI.listCreditInvoices();
      setCreditInvoices(response.data?.invoices || []);
    } catch (error) {
      setCreditInvoiceAlert({
        severity: 'error',
        message: error.response?.data?.message || 'Failed to load credit invoices.',
      });
    } finally {
      setCreditInvoicesLoading(false);
    }
  }, []);

  const requestCreditInvoice = async () => {
    const quantity = Number.parseInt(creditInvoiceForm.quantity, 10);
    const unitPriceCents = creditOverview?.creditPricing?.unitPriceCents || 0;
    if (!Number.isInteger(quantity) || quantity <= 0) {
      setCreditInvoiceAlert({ severity: 'warning', message: 'Enter a valid credit quantity.' });
      return;
    }
    if (!unitPriceCents) {
      setCreditInvoiceAlert({ severity: 'warning', message: 'Credit pricing has not been configured for your university yet.' });
      return;
    }

    setRequestingInvoice(true);
    setCreditInvoiceAlert(null);
    try {
      await tenantAdminAPI.requestCreditInvoice({ quantity });
      setCreditInvoiceForm(defaultCreditInvoiceForm);
      setCreditInvoiceAlert({ severity: 'success', message: 'Invoice requested. We will notify your finance contact.' });
      await loadCreditInvoices();
      await loadCreditOverview();
    } catch (error) {
      setCreditInvoiceAlert({
        severity: 'error',
        message: error.response?.data?.message || 'Failed to request invoice.',
      });
    } finally {
      setRequestingInvoice(false);
    }
  };

  const updateAllocationMode = async () => {
    if (!isAdmin) {
      return;
    }
    setAllocationModeSaving(true);
    setCreditOverviewAlert(null);
    try {
      await tenantAdminAPI.updateCreditAllocationMode({ creditAllocationMode: allocationModeDraft });
      setCreditOverview((prev) => (prev ? { ...prev, creditAllocationMode: allocationModeDraft } : prev));
      setCreditOverviewAlert({ severity: 'success', message: 'Allocation mode updated.' });
    } catch (error) {
      setCreditOverviewAlert({
        severity: 'error',
        message: error.response?.data?.message || 'Failed to update allocation mode.',
      });
    } finally {
      setAllocationModeSaving(false);
    }
  };

  const splitIdentifiers = (value = '') => value
    .split(/[\s,;\n\r]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  const submitCreditAllocation = async () => {
    const amount = Number.parseInt(creditAllocationForm.amount, 10);
    if (!Number.isInteger(amount) || amount <= 0) {
      setCreditAllocationAlert({ severity: 'warning', message: 'Enter a valid credit amount.' });
      return;
    }

    const payload = {
      targetType: creditAllocationForm.targetType,
      amount,
      note: creditAllocationForm.note,
    };

    if (creditAllocationForm.targetType === 'cohort') {
      if (!creditAllocationForm.groupIds.length) {
        setCreditAllocationAlert({ severity: 'warning', message: 'Select at least one cohort.' });
        return;
      }
      payload.groupIds = creditAllocationForm.groupIds;
    } else {
      const identifiers = splitIdentifiers(creditAllocationForm.identifiers);
      if (!identifiers.length) {
        setCreditAllocationAlert({ severity: 'warning', message: 'Add at least one email or user ID.' });
        return;
      }
      payload.identifiers = identifiers;
    }

    setCreditAllocationLoading(true);
    setCreditAllocationAlert(null);
    setCreditAllocationResult(null);
    try {
      const response = await tenantAdminAPI.allocateCredits(payload);
      const data = response.data || response;
      setCreditAllocationResult(data);
      setCreditAllocationAlert({ severity: 'success', message: 'Credits allocated successfully.' });
      await loadCreditOverview();
    } catch (error) {
      setCreditAllocationAlert({
        severity: 'error',
        message: error.response?.data?.message || 'Failed to allocate credits.',
      });
    } finally {
      setCreditAllocationLoading(false);
    }
  };

  const downloadCreditInvoicePdf = async (invoiceId) => {
    if (!invoiceId) {
      return;
    }
    try {
      const response = await tenantAdminAPI.downloadCreditInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credit-invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setCreditInvoiceAlert({
        severity: 'error',
        message: error.response?.data?.message || 'Failed to download invoice PDF.',
      });
    }
  };

  const handleSendRosterInvites = async (importId) => {
    if (!importId) {
      return;
    }
    setSendingRosterInvites(importId);
    try {
      const response = await tenantAdminAPI.sendRosterInvites(importId);
      const totals = response.data?.totals;
      const summary = totals
        ? `Sent ${totals.sent} invites (${totals.failed} failed, ${totals.skipped} skipped).`
        : 'Roster invites sent.';
      showRosterAlert('success', summary);
      await loadInvites();
    } catch (error) {
      showRosterAlert('error', error.response?.data?.message || 'Failed to send roster invites.');
    } finally {
      setSendingRosterInvites(null);
    }
  };

  useEffect(() => {
    if (tab !== 0) {
      return;
    }

    const timeout = setTimeout(() => {
      loadMembers();
    }, 300);

    return () => clearTimeout(timeout);
  }, [tab, loadMembers]);

  useEffect(() => {
    loadGroups();
    loadDashboard();
    loadRosterHistory();
  }, [loadDashboard, loadGroups, loadRosterHistory]);

  useEffect(() => {
    if (tab === 2) {
      loadInvitePolicy();
      loadInvites();
    }
  }, [tab, loadInvitePolicy, loadInvites]);

  useEffect(() => {
    if (tab === 4) {
      loadCreditOverview();
      loadCreditInvoices();
    }
  }, [tab, loadCreditOverview, loadCreditInvoices]);

  useEffect(() => {
    if (tab === COMMUNITY_TAB_INDEX) {
      loadCaseManagementSettings();
      loadCommunityQueue();
    }
  }, [tab, loadCaseManagementSettings, loadCommunityQueue]);

  const openCreateGroup = () => {
    setGroupDialogMode('create');
    setGroupForm(emptyGroupForm);
    setActiveGroupId(null);
    setGroupDialogOpen(true);
  };

  const openEditGroup = (group) => {
    setGroupDialogMode('edit');
    setActiveGroupId(group.id);
    setGroupForm({
      name: group.name || '',
      description: group.description || '',
      cohortCode: group.cohortCode || '',
      academicYear: group.academicYear || '',
      tags: group.tags || [],
      schedule: {
        startsAt: group.schedule?.startsAt ? dayjs(group.schedule.startsAt).format('YYYY-MM-DD') : '',
        endsAt: group.schedule?.endsAt ? dayjs(group.schedule.endsAt).format('YYYY-MM-DD') : '',
        cadence: group.schedule?.cadence || '',
      },
    });
    setGroupDialogOpen(true);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setGroupForm(emptyGroupForm);
    setActiveGroupId(null);
  };

  const handleGroupFormChange = (field, value) => {
    setGroupForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleScheduleChange = (field, value) => {
    setGroupForm((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: value,
      },
    }));
  };

  const submitGroupForm = async () => {
    if (!groupForm.name.trim()) {
      showGroupAlert('warning', 'Group name is required');
      return;
    }

    const payload = {
      ...groupForm,
      tags: groupForm.tags.filter(Boolean),
      schedule: {
        startsAt: groupForm.schedule.startsAt || null,
        endsAt: groupForm.schedule.endsAt || null,
        cadence: groupForm.schedule.cadence,
      },
    };

    try {
      if (groupDialogMode === 'edit' && activeGroupId) {
        await groupAPI.update(activeGroupId, payload);
        showGroupAlert('success', 'Group updated');
      } else {
        await groupAPI.create(payload);
        showGroupAlert('success', 'Group created');
      }
      closeGroupDialog();
      loadGroups();
    } catch (error) {
      showGroupAlert('error', error.response?.data?.message || 'Failed to save group');
    }
  };

  const archiveGroup = async (groupId) => {
    if (!window.confirm('Archive this group? Members will remain but the cohort will be hidden.')) {
      return;
    }
    try {
      await groupAPI.archive(groupId);
      showGroupAlert('success', 'Group archived');
      loadGroups();
    } catch (error) {
      showGroupAlert('error', error.response?.data?.message || 'Failed to archive group');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setRosterLoading(true);
    try {
      const text = await file.text();
      const response = await tenantAdminAPI.importRoster({ csv: text, filename: file.name });
      setRosterPreview(response.data);
      setRosterImportId(response.data.importId);
      const selected = new Set((response.data.preview || [])
        .filter((row) => !row.issues?.length)
        .map((row) => row.email));
      setRosterSelections(selected);
      showRosterAlert('success', `Imported ${response.data.summary.validRows} valid learners`);
      loadRosterHistory();
      loadDashboard();
    } catch (error) {
      showRosterAlert('error', error.response?.data?.message || 'Failed to process roster');
    } finally {
      setRosterLoading(false);
      event.target.value = '';
    }
  };

  const toggleRosterSelection = (email) => {
    setRosterSelections((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const commitRoster = async () => {
    if (!rosterImportId) {
      showRosterAlert('warning', 'Upload a roster before committing');
      return;
    }

    setRosterLoading(true);
    try {
      await tenantAdminAPI.commitRosterImport(rosterImportId, {
        rowSelections: Array.from(rosterSelections),
        createMissingGroups: rosterAutoCreate,
        defaultGroupIds: rosterDefaultGroups,
        sendInvites: false,
      });
      showRosterAlert('success', 'Roster processed successfully');
      setRosterPreview(null);
      setRosterImportId(null);
      setRosterSelections(new Set());
      loadGroups();
      loadRosterHistory();
      loadDashboard();
    } catch (error) {
      showRosterAlert('error', error.response?.data?.message || 'Failed to assign roster');
    } finally {
      setRosterLoading(false);
    }
  };

  const downloadRosterErrors = async (importId) => {
    if (!importId) {
      return;
    }

    try {
      const response = await tenantAdminAPI.downloadRosterErrors(importId);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `roster-errors-${importId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showRosterAlert('error', error.response?.data?.message || 'Failed to download roster errors');
    }
  };

  const dashboardCards = useMemo(() => {
    if (!dashboard) return [];
    return [
      { title: 'Active groups', value: dashboard.headline?.groupCount || 0 },
      { title: 'Active cases', value: dashboard.headline?.activeCases || 0 },
      { title: 'Roster imports pending', value: dashboard.headline?.pendingRosterImports || 0 },
    ];
  }, [dashboard]);

  const rosterPreviewRows = rosterPreview?.preview || [];
  const creditUnitPriceCents = creditOverview?.creditPricing?.unitPriceCents || 0;
  const requestedQuantity = Number.parseInt(creditInvoiceForm.quantity, 10);
  const safeQuantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 0;
  const estimatedSubtotalCents = Math.max(0, Math.round(safeQuantity * creditUnitPriceCents));
  const effectiveVatRate = creditOverview?.vatEnabled ? (creditOverview?.vatRate || 0) : 0;
  const estimatedVatCents = Math.max(0, Math.round(estimatedSubtotalCents * effectiveVatRate));
  const estimatedTotalCents = estimatedSubtotalCents + estimatedVatCents;
  const caseSettingsValue = caseManagementDraft || defaultCaseManagementSettings;
  const caseSettingsReady = Boolean(caseManagementDraft);
  const handleTabKeyDown = useCallback((event) => {
    if (event.defaultPrevented) {
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    if (isEditableElement(event.target)) {
      return;
    }
    event.preventDefault();
    setTab((prev) => {
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = prev + delta;
      if (next < 0) {
        return 0;
      }
      if (next >= TAB_COUNT) {
        return TAB_COUNT - 1;
      }
      return next;
    });
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }} onKeyDown={handleTabKeyDown}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        University admin console
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage cohorts, invitations, rosters, and tenancy health in one place.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton aria-label="Previous tab" onClick={() => setTab((prev) => Math.max(prev - 1, 0))} disabled={tab === 0}>
          <ChevronLeftIcon />
        </IconButton>
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          aria-label="University admin tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ flex: 1 }}
        >
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Roster" />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Groups & cohorts" />
          <Tab icon={<MailIcon />} iconPosition="start" label="Invitations" />
          <Tab icon={<UploadIcon />} iconPosition="start" label="Roster import" />
          <Tab icon={<PaidIcon />} iconPosition="start" label="Credits" />
          <Tab icon={<InsightsIcon />} iconPosition="start" label="Analytics overview" />
          <Tab icon={<BarChartIcon />} iconPosition="start" label="Performance dashboards" />
          <Tab icon={<PublicIcon />} iconPosition="start" label="Community & cases" />
        </Tabs>
        <IconButton aria-label="Next tab" onClick={() => setTab((prev) => Math.min(prev + 1, TAB_COUNT - 1))} disabled={tab === TAB_COUNT - 1}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <TabPanel value={tab} index={0}>
        {memberAlert && (
          <Alert severity={memberAlert.severity} sx={{ mb: 2 }}>
            {memberAlert.message}
          </Alert>
        )}
        {memberError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {memberError}
            <Button size="small" sx={{ ml: 2 }} startIcon={<RefreshIcon />} onClick={() => loadMembers()}>
              Retry
            </Button>
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Search members"
            value={memberFilters.search}
            onChange={handleMemberSearchChange}
            placeholder="Search by name or email"
            fullWidth
          />
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="member-role-filter">Role</InputLabel>
            <Select
              labelId="member-role-filter"
              label="Role"
              value={memberFilters.role}
              onChange={(event) => handleMemberFilterChange('role', event.target.value)}
            >
              {memberRoleFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="member-status-filter">Status</InputLabel>
            <Select
              labelId="member-status-filter"
              label="Status"
              value={memberFilters.status}
              onChange={(event) => handleMemberFilterChange('status', event.target.value)}
            >
              {memberStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="member-group-filter">Cohort</InputLabel>
            <Select
              labelId="member-group-filter"
              label="Cohort"
              value={memberFilters.groupId}
              onChange={(event) => handleMemberFilterChange('groupId', event.target.value)}
            >
              <MenuItem value="all">All cohorts</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="member-sort-filter">Sort by</InputLabel>
            <Select
              labelId="member-sort-filter"
              label="Sort by"
              value={memberFilters.sort}
              onChange={(event) => handleMemberFilterChange('sort', event.target.value)}
            >
              {memberSortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadMembers()}>
            Refresh
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {memberSummaryCards.map((card) => (
            <Grid item xs={6} sm={4} md={2} key={card.label}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {memberSelectionState.selectedCount > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={(
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={openMemberRoleDialog}
                  disabled={memberActionLoading || !selectedUsers.length}
                >
                  Change role
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={openMemberGroupDialog}
                  disabled={memberActionLoading || !selectedUsers.length}
                >
                  Update cohorts
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleResendSelectedInvites}
                  disabled={inviteBulkLoading || !selectedInvites.length}
                >
                  Resend invites
                </Button>
                <Button size="small" onClick={clearMemberSelection}>Clear selection</Button>
              </Stack>
            )}
          >
            {memberSelectionState.selectedCount} selected ({selectedUsers.length} members, {selectedInvites.length} invites)
          </Alert>
        )}

        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={memberSelectionState.allSelected}
                      indeterminate={memberSelectionState.indeterminate}
                      onChange={(event) => toggleSelectAllMembers(event.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cohorts</TableCell>
                  <TableCell>Last activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memberLoading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!memberLoading && sortedMembers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No members found for the selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!memberLoading && sortedMembers.map((member) => {
                  const key = getMemberKey(member);
                  const selected = memberSelection.has(key);
                  const cohorts = member.type === 'invite'
                    ? (member.groupNames || member.metadata?.groupNames || [])
                    : (member.groups || []).map((group) => group.name).filter(Boolean);
                  const statusLabel = member.type === 'invite'
                    ? (member.status === 'pending' ? 'Invite pending' : member.status)
                    : (member.status === 'inactive' ? 'Inactive' : 'Active');
                  const activity = member.type === 'invite'
                    ? formatDateTime(member.createdAt)
                    : formatDateTime(member.lastLoginAt || member.updatedAt || member.createdAt);

                  return (
                    <TableRow key={key} hover selected={selected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selected}
                          onChange={() => toggleMemberSelection(member)}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.3}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email || 'Unknown'}
                          </Typography>
                          {member.type === 'invite' && (
                            <Typography variant="caption" color="text.secondary">
                              Invite
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{member.email || '-'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{member.role || (member.type === 'invite' ? 'invite' : '-')}</TableCell>
                      <TableCell>{statusLabel}</TableCell>
                      <TableCell>
                        {cohorts && cohorts.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {cohorts.map((name) => (
                              <Chip key={name} label={name} size="small" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>{activity}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {groupAlert && (
          <Alert severity={groupAlert.severity} sx={{ mb: 2 }}>
            {groupAlert.message}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateGroup}>
            New group
          </Button>
        </Box>
        <Grid container spacing={2}>
          {groups.map((group) => (
            <Grid item xs={12} md={6} lg={4} key={group.id}>
              <Card variant="outlined">
                <CardHeader
                  title={group.name}
                  subheader={group.cohortCode ? `Cohort ${group.cohortCode}` : 'Custom cohort'}
                  action={group.tags?.length ? (
                    <Stack direction="row" spacing={1}>
                      {group.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                      {group.tags.length > 3 && <Chip size="small" label={`+${group.tags.length - 3}`} />}
                    </Stack>
                  ) : null}
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {group.description || 'No description provided'}
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">Academic year: {group.academicYear || '-'}</Typography>
                    <Typography variant="body2">
                      Starts {formatDate(group.schedule?.startsAt)} - Ends {formatDate(group.schedule?.endsAt)}
                    </Typography>
                    <Typography variant="body2">Cadence: {group.schedule?.cadence || 'Not set'}</Typography>
                    <Typography variant="body2">Members tracked: {group.memberIds?.length || 0}</Typography>
                  </Stack>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => openEditGroup(group)}>Edit</Button>
                  <Button size="small" color="error" onClick={() => archiveGroup(group.id)} startIcon={<DeleteIcon fontSize="small" />}>
                    Archive
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {groups.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">No groups created yet. Use the button above to create your first cohort.</Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>
      <TabPanel value={tab} index={2}>
        {inviteAlert && (
          <Alert severity={inviteAlert.severity} sx={{ mb: 2 }}>
            {inviteAlert.message}
          </Alert>
        )}
        {invitePolicyError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {invitePolicyError}
            <Button
              size="small"
              sx={{ ml: 2 }}
              startIcon={<RefreshIcon />}
              onClick={loadInvitePolicy}
            >
              Retry
            </Button>
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader
                title="Send a new invite"
                subheader="Invite learners or instructors directly, or import a roster first."
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email"
                      fullWidth
                      value={inviteForm.email}
                      onChange={(event) => handleInviteFormChange('email', event.target.value)}
                      placeholder="student@example.edu"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="invite-role-label">Role</InputLabel>
                      <Select
                        labelId="invite-role-label"
                        label="Role"
                        value={inviteForm.role}
                        onChange={(event) => handleInviteFormChange('role', event.target.value)}
                      >
                        {inviteRoleOptions
                          .filter((option) => option.value !== 'all')
                          .map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="First name (optional)"
                      fullWidth
                      value={inviteForm.firstName}
                      onChange={(event) => handleInviteFormChange('firstName', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Last name (optional)"
                      fullWidth
                      value={inviteForm.lastName}
                      onChange={(event) => handleInviteFormChange('lastName', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Expires in (days)"
                      type="number"
                      fullWidth
                      value={inviteForm.expiresInDays}
                      onChange={(event) => handleInviteFormChange('expiresInDays', event.target.value)}
                      inputProps={{ min: 1, max: 90 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 2, md: 1 } }}>
                      Invites expire automatically. You can resend or revoke them at any time from the table below.
                    </Typography>
                  </Grid>
                </Grid>
                {lastInviteLink && (
                  <Alert
                    severity="success"
                    sx={{ mt: 2 }}
                    action={(
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<CopyIcon fontSize="small" />}
                        onClick={() => handleCopyInviteLink(lastInviteLink)}
                      >
                        Copy link
                      </Button>
                    )}
                  >
                    Invitation link generated. Share with care.
                  </Alert>
                )}
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleCreateInvite}
                  disabled={creatingInvite}
                >
                  {creatingInvite ? 'Sending...' : 'Send invite'}
                </Button>
                <Button
                  variant="text"
                  startIcon={<RefreshIcon />}
                  onClick={loadInvites}
                  disabled={invitesLoading}
                >
                  Refresh list
                </Button>
              </CardActions>
            </Card>
            <Card variant="outlined">
              <CardHeader
                title="Invites"
                action={(
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="invite-status-filter">Status</InputLabel>
                      <Select
                        labelId="invite-status-filter"
                        label="Status"
                        value={inviteFilters.status}
                        onChange={(event) => handleInviteFilterChange('status', event.target.value)}
                      >
                        {inviteStatusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="invite-role-filter">Role</InputLabel>
                      <Select
                        labelId="invite-role-filter"
                        label="Role"
                        value={inviteFilters.role}
                        onChange={(event) => handleInviteFilterChange('role', event.target.value)}
                      >
                        {inviteRoleOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadInvites}
                      disabled={invitesLoading}
                    >
                      Refresh
                    </Button>
                  </Stack>
                )}
              />
              <CardContent>
                {invitesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : invites.length === 0 ? (
                  <Alert severity="info">No invites match the selected filters.</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Last sent</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invites.map((invite) => (
                        <TableRow key={invite.id || invite.email} hover>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{invite.role || 'student'}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{invite.status || 'pending'}</TableCell>
                          <TableCell>{formatDate(invite.expiresAt)}</TableCell>
                          <TableCell>{formatDateTime(invite.lastSentAt)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                startIcon={<SendIcon fontSize="small" />}
                                onClick={() => handleResendInvite(invite.id)}
                                disabled={inviteActions.resending === invite.id}
                              >
                                {inviteActions.resending === invite.id ? 'Resending...' : 'Resend'}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<RejectIcon fontSize="small" />}
                                onClick={() => handleRevokeInvite(invite.id)}
                                disabled={inviteActions.revoking === invite.id}
                              >
                                {inviteActions.revoking === invite.id ? 'Revoking...' : 'Revoke'}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader title="Invitation policy" />
              <CardContent>
                {invitePolicyLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : invitePolicy ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Invites required:&nbsp;
                      <strong>{invitePolicy.registration.requireInvite ? 'Yes' : 'No'}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Domain self-registration:&nbsp;
                      <strong>{invitePolicy.registration.domainSignupEnabled ? 'Enabled' : 'Disabled'}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Pending invites:&nbsp;
                      <strong>{invitePolicy.invites.counts.pending}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Accepted invites:&nbsp;
                      <strong>{invitePolicy.invites.counts.accepted}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Expired invites:&nbsp;
                      <strong>{invitePolicy.invites.counts.expired}</strong>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Registration settings are not available yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value={tab} index={3}>
        {rosterAlert && (
          <Alert severity={rosterAlert.severity} sx={{ mb: 2 }}>
            {rosterAlert.message}
          </Alert>
        )}
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ alignItems: { md: 'center' }, mb: 3 }}>
          <Button variant="contained" component="label" startIcon={<UploadIcon />} disabled={rosterLoading}>
            Upload CSV roster
            <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
          </Button>
          <FormControlLabel
            control={(
              <Switch
                checked={rosterAutoCreate}
                onChange={(_event, checked) => setRosterAutoCreate(checked)}
              />
            )}
            label="Create missing cohorts from roster"
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="roster-default-groups">Default groups</InputLabel>
            <Select
              labelId="roster-default-groups"
              multiple
              value={rosterDefaultGroups}
              label="Default groups"
              onChange={(event) => setRosterDefaultGroups(event.target.value)}
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={loadRosterHistory}>Refresh history</Button>
        </Stack>

        {rosterLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {rosterPreview && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title="Roster preview" subheader={`${rosterPreview.summary.validRows} ready - ${rosterPreview.summary.invalidRows} with issues`} />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Groups</TableCell>
                    <TableCell align="right">Include</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rosterPreviewRows.map((row) => {
                    const hasIssues = row.issues?.length;
                    const selected = rosterSelections.has(row.email);
                    return (
                      <TableRow key={row.email} selected={selected} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{row.email}</Typography>
                            {hasIssues && (
                              <Tooltip title={row.issues.join('\n')}>
                                <WarningIcon color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{row.firstName} {row.lastName}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{row.role || 'student'}</TableCell>
                        <TableCell>{row.yearLevel || '-'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {(row.groupNames || []).map((name) => (
                              <Chip key={name} label={name} size="small" />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Switch
                            checked={selected}
                            disabled={hasIssues}
                            onChange={() => toggleRosterSelection(row.email)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={commitRoster}
                disabled={rosterSelections.size === 0 || rosterLoading}
              >
                Assign selected learners
              </Button>
            </CardActions>
          </Card>
        )}

        <Card variant="outlined">
          <CardHeader title="Recent roster runs" />
          <CardContent>
            <List>
              {rosterHistory.map((entry) => (
                <ListItem key={entry.id} divider disableGutters>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ width: '100%', px: 2, py: 1 }}
                  >
                    <ListItemText
                      primary={`${entry.originalFilename || 'Roster upload'} - ${entry.status}`}
                      secondary={`Rows: ${entry.rowCount} - Processed: ${entry.processedCount} - Invites: ${entry.createdInvites} - Updated users: ${entry.updatedUsers}`}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ flexShrink: 0 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(entry.createdAt).format('MMM D, YYYY HH:mm')}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {entry.createdInvites > 0 && entry.status === 'processed' && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<SendIcon fontSize="small" />}
                            onClick={() => handleSendRosterInvites(entry.id)}
                            disabled={sendingRosterInvites === entry.id}
                          >
                            {sendingRosterInvites === entry.id ? 'Sending...' : 'Send invites'}
                          </Button>
                        )}
                        {entry.errorCount > 0 && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<ImportIcon fontSize="small" />}
                            onClick={() => downloadRosterErrors(entry.id)}
                          >
                            Download errors
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </ListItem>
              ))}
              {rosterHistory.length === 0 && (
                <ListItem>
                  <ListItemText primary="No roster imports yet." />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={5}>
        {dashboardLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : dashboardAlert ? (
          <Alert severity="error">{dashboardAlert}</Alert>
        ) : (
          <Grid container spacing={3}>
            {dashboardCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.title}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">{card.title}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Active cohorts" />
                <CardContent>
                  <List>
                    {dashboard?.cohorts?.map((cohort) => (
                      <ListItem key={cohort.id} divider>
                        <ListItemText
                          primary={cohort.name}
                          secondary={`Year ${cohort.academicYear || '-'} - ${formatDate(cohort.startsAt)} - ${formatDate(cohort.endsAt)}`}
                        />
                      </ListItem>
                    ))}
                    {(dashboard?.cohorts?.length || 0) === 0 && (
                      <ListItem>
                        <ListItemText primary="No active cohorts scheduled." />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Upcoming expirations" />
                <CardContent>
                  <List>
                    {dashboard?.upcomingExpirations?.map((entry) => (
                      <ListItem key={entry.id} divider>
                        <ListItemText
                          primary={entry.title}
                          secondary={`Expires ${formatDate(entry.endsAt)}`}
                        />
                      </ListItem>
                    ))}
                    {(dashboard?.upcomingExpirations?.length || 0) === 0 && (
                      <ListItem>
                        <ListItemText primary="No expirations in the next 14 days." />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Access anomalies" />
                <CardContent>
                  {dashboard?.anomalies?.length ? (
                    <List>
                      {dashboard.anomalies.map((entry, index) => (
                        <ListItem key={`${entry.type}-${index}`} divider>
                          <ListItemText
                            primary={entry.message}
                            secondary={dayjs(entry.createdAt).format('MMM D, YYYY HH:mm')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No anomalies detected from recent roster imports.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tab} index={6}>
        <StudentPerformanceDashboard />
      </TabPanel>

      <TabPanel value={tab} index={4}>
        {creditOverviewAlert && (
          <Alert severity={creditOverviewAlert.severity} sx={{ mb: 2 }}>
            {creditOverviewAlert.message}
          </Alert>
        )}
        {creditInvoiceAlert && (
          <Alert severity={creditInvoiceAlert.severity} sx={{ mb: 2 }}>
            {creditInvoiceAlert.message}
          </Alert>
        )}
        {creditAllocationAlert && (
          <Alert severity={creditAllocationAlert.severity} sx={{ mb: 2 }}>
            {creditAllocationAlert.message}
          </Alert>
        )}
        {creditOverviewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !creditOverview ? (
          <Alert severity="warning">Unable to load credit settings.</Alert>
        ) : (
          <Stack spacing={3}>
            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Credit balance</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Current balance</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatNumber(creditOverview.wallet?.balance || 0)} credits
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Currency: {(creditOverview.wallet?.currency || creditOverview.creditPricing?.currency || 'GBP').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Price per history</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(creditOverview.creditPricing?.unitPriceCents || 0, creditOverview.creditPricing?.currency || 'GBP')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    VAT {creditOverview.vatEnabled ? `${Math.round((creditOverview.vatRate || 0) * 100)}%` : 'not applied'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Allocation mode</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {creditOverview.creditAllocationMode === 'shared_pool' ? 'Shared pool' : 'Assigned to students'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Instructors have unlimited access.
                  </Typography>
                </Grid>
              </Grid>
              {isAdmin ? (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <FormControl sx={{ minWidth: 260 }}>
                      <InputLabel id="allocation-mode-label">Allocation mode</InputLabel>
                      <Select
                        labelId="allocation-mode-label"
                        label="Allocation mode"
                        value={allocationModeDraft}
                        onChange={(event) => setAllocationModeDraft(event.target.value)}
                      >
                        {allocationModeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={updateAllocationMode}
                      disabled={allocationModeSaving || allocationModeDraft === creditOverview.creditAllocationMode}
                    >
                      {allocationModeSaving ? 'Saving...' : 'Save allocation mode'}
                    </Button>
                  </Stack>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Allocation mode can be updated by a university administrator.
                </Typography>
              )}
            </Paper>

            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Buy credits</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Credit quantity"
                    type="number"
                    value={creditInvoiceForm.quantity}
                    onChange={(event) => setCreditInvoiceForm({ quantity: event.target.value })}
                    inputProps={{ min: 1 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      {`${formatNumber(Number.parseInt(creditInvoiceForm.quantity, 10) || 0)} credits × ${formatCurrency(creditOverview.creditPricing?.unitPriceCents || 0, creditOverview.creditPricing?.currency || 'GBP')}`}
                    </Typography>
                    <Typography variant="body2">
                      Subtotal: {formatCurrency(estimatedSubtotalCents, creditOverview.creditPricing?.currency || 'GBP')}
                    </Typography>
                    {creditOverview.vatEnabled && estimatedVatCents > 0 && (
                      <Typography variant="body2">
                        VAT ({Math.round((creditOverview.vatRate || 0) * 100)}%): {formatCurrency(estimatedVatCents, creditOverview.creditPricing?.currency || 'GBP')}
                      </Typography>
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Total: {formatCurrency(estimatedTotalCents, creditOverview.creditPricing?.currency || 'GBP')}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={requestCreditInvoice}
                  disabled={requestingInvoice}
                >
                  {requestingInvoice ? 'Requesting...' : 'Request invoice'}
                </Button>
                <Button variant="outlined" onClick={loadCreditInvoices} disabled={creditInvoicesLoading}>
                  Refresh invoices
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Invoices are emailed to your finance contact once approved by Global Admin.
              </Typography>
            </Paper>

            {creditOverview.creditAllocationMode === 'assigned' && (
              <Paper elevation={0} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Allocate credits to students</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Allocation type"
                      value={creditAllocationForm.targetType}
                      onChange={(event) => setCreditAllocationForm((prev) => ({
                        ...prev,
                        targetType: event.target.value,
                        groupIds: [],
                        identifiers: '',
                      }))}
                      fullWidth
                    >
                      <MenuItem value="cohort">By cohort</MenuItem>
                      <MenuItem value="individual">By individual</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={creditAllocationForm.targetType === 'cohort' ? 'Total credits to distribute' : 'Credits per student'}
                      type="number"
                      value={creditAllocationForm.amount}
                      onChange={(event) => setCreditAllocationForm((prev) => ({ ...prev, amount: event.target.value }))}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Notes (optional)"
                      value={creditAllocationForm.note}
                      onChange={(event) => setCreditAllocationForm((prev) => ({ ...prev, note: event.target.value }))}
                      fullWidth
                    />
                  </Grid>
                  {creditAllocationForm.targetType === 'cohort' ? (
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Cohorts"
                        value={creditAllocationForm.groupIds}
                        onChange={(event) => setCreditAllocationForm((prev) => ({ ...prev, groupIds: event.target.value }))}
                        SelectProps={{ multiple: true }}
                        fullWidth
                      >
                        {groups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Total credits are split evenly across students in the selected cohorts.
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <TextField
                        label="Student emails or IDs"
                        value={creditAllocationForm.identifiers}
                        onChange={(event) => setCreditAllocationForm((prev) => ({ ...prev, identifiers: event.target.value }))}
                        multiline
                        minRows={3}
                        placeholder="student1@campus.edu, student2@campus.edu"
                        fullWidth
                      />
                    </Grid>
                  )}
                </Grid>
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={submitCreditAllocation}
                    disabled={creditAllocationLoading}
                  >
                    {creditAllocationLoading ? 'Allocating...' : 'Allocate credits'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setCreditAllocationForm(defaultCreditAllocationForm)}
                    disabled={creditAllocationLoading}
                  >
                    Clear form
                  </Button>
                </Stack>
                {creditAllocationResult && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {`Allocated ${creditAllocationResult.grantedTotal || 0} credits to ${creditAllocationResult.totals?.succeeded || 0} students.`}
                  </Alert>
                )}
              </Paper>
            )}

            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Invoice history</Typography>
              {creditInvoicesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : creditInvoices.length === 0 ? (
                <Alert severity="info">No credit invoices yet.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Credits</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Requested</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {creditInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{invoice.status}</TableCell>
                        <TableCell>{formatNumber(invoice.quantity)}</TableCell>
                        <TableCell>{formatCurrency(invoice.totalCents, invoice.currency)}</TableCell>
                        <TableCell>{formatDate(invoice.requestedAt)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {invoice.status !== 'paid' && invoice.stripeHostedInvoiceUrl && (
                              <Button
                                size="small"
                                component="a"
                                href={invoice.stripeHostedInvoiceUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Pay online
                              </Button>
                            )}
                            <Button
                              size="small"
                              startIcon={<ImportIcon fontSize="small" />}
                              onClick={() => downloadCreditInvoicePdf(invoice.id)}
                            >
                              PDF
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Stack>
        )}
      </TabPanel>

      <TabPanel value={tab} index={COMMUNITY_TAB_INDEX}>
        {caseManagementAlert && (
          <Alert severity={caseManagementAlert.severity} sx={{ mb: 2 }}>
            {caseManagementAlert.message}
          </Alert>
        )}
        {communityQueueAlert && (
          <Alert severity={communityQueueAlert.severity} sx={{ mb: 2 }}>
            {communityQueueAlert.message}
          </Alert>
        )}

        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Case access and community rules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Control who can edit the tenant library, what learners see in Unity, and how community publishing works.
            </Typography>

            {caseManagementLoading && !caseSettingsReady ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">Loading settings...</Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Typography variant="subtitle1">Case access</Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.allowStudentLibraryEdits}
                      onChange={(event) => mergeCaseManagementDraft({ allowStudentLibraryEdits: event.target.checked })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Allow students to edit tenant library cases"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.allowPersonalLibraryInUnity}
                      onChange={(event) => mergeCaseManagementDraft({ allowPersonalLibraryInUnity: event.target.checked })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Allow personal case libraries in Unity"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.allowDirectVirtualMedic}
                      onChange={(event) => mergeCaseManagementDraft({ allowDirectVirtualMedic: event.target.checked })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Allow direct Virtual Medic library access for learners"
                />

                <Divider sx={{ my: 1 }} />

                <Typography variant="subtitle1">Community publishing</Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.community.allowTenantCommunity}
                      onChange={(event) => mergeCaseManagementDraft({ community: { allowTenantCommunity: event.target.checked } })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Enable university community listings"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.community.allowGlobalCommunity}
                      onChange={(event) => mergeCaseManagementDraft({ community: { allowGlobalCommunity: event.target.checked } })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Enable global community listings"
                />
                <FormControl size="small" sx={{ maxWidth: 320 }}>
                  <InputLabel id="student-publishing-scope">Student publishing scope</InputLabel>
                  <Select
                    labelId="student-publishing-scope"
                    label="Student publishing scope"
                    value={caseSettingsValue.community.studentPublishingScope}
                    onChange={(event) => mergeCaseManagementDraft({ community: { studentPublishingScope: event.target.value } })}
                    disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                  >
                    <MenuItem value="none">Not allowed</MenuItem>
                    <MenuItem value="tenant">University community only</MenuItem>
                    <MenuItem value="global">Global community</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.community.requireFacultyApproval}
                      onChange={(event) => mergeCaseManagementDraft({ community: { requireFacultyApproval: event.target.checked } })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Require faculty approval for student submissions"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={caseSettingsValue.community.allowImports}
                      onChange={(event) => mergeCaseManagementDraft({ community: { allowImports: event.target.checked } })}
                      disabled={!isAdmin || caseManagementSaving || !caseSettingsReady}
                    />
                  )}
                  label="Allow imports from the community library"
                />
              </Stack>
            )}

            {!isAdmin && (
              <Alert severity="info" sx={{ mt: 3 }}>
                Only university admins can update case access and community settings.
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={saveCaseManagementSettings}
                disabled={!isAdmin || !caseSettingsReady || caseManagementSaving}
              >
                {caseManagementSaving ? 'Saving...' : 'Save settings'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => caseManagement && setCaseManagementDraft(caseManagement)}
                disabled={!caseManagement || caseManagementSaving}
              >
                Reset changes
              </Button>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">Community moderation</Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadCommunityQueue}
                disabled={communityQueueLoading}
              >
                Refresh queue
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review pending submissions and reported community cases from your university.
            </Typography>

            {communityQueueLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">Loading moderation queue...</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Pending approvals</Typography>
                  {communityQueue.pending.length === 0 ? (
                    <Alert severity="info">No pending cases right now.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {communityQueue.pending.map((caseItem) => {
                        const stats = caseItem.community?.stats || {};
                        const tags = Array.isArray(caseItem.tags) ? caseItem.tags.slice(0, 3) : [];
                        const submittedAt = caseItem.community?.publishedAt || caseItem.updatedAt || caseItem.createdAt;
                        return (
                          <Paper key={caseItem._id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1}>
                              <Typography variant="subtitle1">{caseItem.title}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                By {getCaseAuthorName(caseItem)} | {caseItem.presentingComplaint || 'No complaint'} | {caseItem.specialty || 'No specialty'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Submitted {formatDateTime(submittedAt)}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip size="small" label={`Rating ${stats.averageRating || 0}`} />
                                <Chip size="small" variant="outlined" label={`${stats.importCount || 0} imports`} />
                                {tags.map((tag) => (
                                  <Chip key={tag} size="small" variant="outlined" label={tag} />
                                ))}
                              </Stack>
                              <TextField
                                size="small"
                                label="Approval notes (optional)"
                                value={communityNoteDrafts[caseItem._id] || ''}
                                onChange={(event) => handleCommunityNoteChange(caseItem._id, event.target.value)}
                                fullWidth
                              />
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  onClick={() => window.open(`/community-library/${caseItem._id}`, '_blank')}
                                >
                                  View listing
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<RejectIcon fontSize="small" />}
                                  onClick={() => handleRejectCommunityCase(caseItem._id)}
                                  disabled={communityActionLoading === caseItem._id}
                                >
                                  Reject
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleApproveCommunityCase(caseItem._id)}
                                  disabled={communityActionLoading === caseItem._id}
                                >
                                  Approve
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Reported cases</Typography>
                  {communityQueue.flagged.length === 0 ? (
                    <Alert severity="info">No reported cases right now.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {communityQueue.flagged.map((caseItem) => {
                        const moderation = caseItem.community?.moderation || {};
                        const reportCount = moderation.reportCount || 0;
                        const lastReportedAt = moderation.lastReportedAt || null;
                        return (
                          <Paper key={caseItem._id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1}>
                              <Typography variant="subtitle1">{caseItem.title}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                By {getCaseAuthorName(caseItem)} | {caseItem.presentingComplaint || 'No complaint'} | {caseItem.specialty || 'No specialty'}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip size="small" color="warning" label={`${reportCount} reports`} />
                                {moderation.flaggedReason && (
                                  <Chip size="small" variant="outlined" label={moderation.flaggedReason} />
                                )}
                              </Stack>
                              {lastReportedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  Last reported {formatDateTime(lastReportedAt)}
                                </Typography>
                              )}
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  onClick={() => window.open(`/community-library/${caseItem._id}`, '_blank')}
                                >
                                  View listing
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleArchiveCommunityCase(caseItem._id)}
                                  disabled={communityActionLoading === caseItem._id}
                                >
                                  Archive listing
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Grid>
              </Grid>
            )}
          </Paper>
        </Stack>
      </TabPanel>

      <Dialog open={memberRoleDialogOpen} onClose={() => setMemberRoleDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Change member role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Apply a new role to the selected members. Instructors gain access to the case manager but not the admin console.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="member-role-dialog-select">Role</InputLabel>
            <Select
              labelId="member-role-dialog-select"
              label="Role"
              value={memberRoleDialogValue}
              onChange={(event) => setMemberRoleDialogValue(event.target.value)}
            >
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="instructor">Instructor</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitMemberRoleDialog} variant="contained" disabled={memberActionLoading}>
            {memberActionLoading ? 'Updating...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={memberGroupDialogOpen} onClose={() => setMemberGroupDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update cohort memberships</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Add or remove the selected members from cohorts. Use replace mode to overwrite existing assignments.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="member-group-select">Cohorts</InputLabel>
            <Select
              labelId="member-group-select"
              label="Cohorts"
              multiple
              value={memberGroupSelection}
              onChange={(event) => setMemberGroupSelection(event.target.value)}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {selected.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No cohorts selected</Typography>
                  ) : selected.map((id) => {
                    const group = groups.find((entry) => entry.id === id);
                    return <Chip key={id} label={group?.name || 'Unknown'} size="small" />;
                  })}
                </Stack>
              )}
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="member-group-mode">Update mode</InputLabel>
            <Select
              labelId="member-group-mode"
              label="Update mode"
              value={memberGroupMode}
              onChange={(event) => setMemberGroupMode(event.target.value)}
            >
              <MenuItem value="append">Add to selected cohorts</MenuItem>
              <MenuItem value="replace">Replace with selected cohorts</MenuItem>
              <MenuItem value="remove" disabled={memberGroupSelection.length === 0}>Remove selected cohorts</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitMemberGroupDialog} variant="contained" disabled={memberActionLoading}>
            {memberActionLoading ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupDialogOpen} onClose={closeGroupDialog} fullWidth maxWidth="sm">
        <DialogTitle>{groupDialogMode === 'edit' ? 'Edit group' : 'Create group'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Group name"
              value={groupForm.name}
              onChange={(event) => handleGroupFormChange('name', event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={groupForm.description}
              onChange={(event) => handleGroupFormChange('description', event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Cohort code"
              value={groupForm.cohortCode}
              onChange={(event) => handleGroupFormChange('cohortCode', event.target.value)}
              fullWidth
            />
            <TextField
              label="Academic year"
              value={groupForm.academicYear}
              onChange={(event) => handleGroupFormChange('academicYear', event.target.value)}
              fullWidth
            />
            <TextField
              label="Tags (comma separated)"
              value={groupForm.tags.join(', ')}
              onChange={(event) => handleGroupFormChange('tags', event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean))}
              fullWidth
            />
            <Divider textAlign="left">Schedule</Divider>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                value={groupForm.schedule.startsAt}
                onChange={(event) => handleScheduleChange('startsAt', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End date"
                type="date"
                value={groupForm.schedule.endsAt}
                onChange={(event) => handleScheduleChange('endsAt', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Cadence"
              value={groupForm.schedule.cadence}
              onChange={(event) => handleScheduleChange('cadence', event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGroupDialog}>Cancel</Button>
          <Button onClick={submitGroupForm} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default UniversityAdminConsole;

